import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import { runValidation } from "../../utils/buchi";
import ValidationError from "../../components/ui/form-elements/ValidaionError";
import toastManager from "../../components/ui/toast/ToasterManager";
import Modal from "../../components/ui/modal/Modal";
import {
  useGetLoanTypes,
  useCreateLoanType,
  useUpdateLoanType,
} from "../../redux/actions/loanTypeAction";
import { SelectSociety } from "./LoanSettings";

const TEMPLATE_OPTIONS = [
  { value: "standard_cooperative", label: "Standard Cooperative Loan" },
  { value: "quick_loan", label: "Quick Loan" },
];

// Server errors can come back either grouped by field (e.g. buchi.js's
// `{ field: ['msg', ...] }`) or as a flat array of strings/`{message}` objects.
// Flatten either shape into a plain list of messages for the top-of-form banner.
const extractErrorMessages = (payload) => {
  const errors = payload?.errors;

  if (Array.isArray(errors)) {
    return errors.map((e) => (typeof e === "string" ? e : e?.message)).filter(Boolean);
  }

  if (errors && typeof errors === "object") {
    return Object.values(errors).flat().filter(Boolean);
  }

  return [payload?.message || "Something went wrong"];
};

const EMPTY_FORM = {
  id: "",
  name: "",
  template: "standard_cooperative",
  minThriftMonths: "",
  thriftPercentageCap: "",
  minAmount: "",
  maxAmount: "",
  isActive: "true",
};

const LoanTypes = () => {
  const { roles, user } = useSelector((state) => state.auth);
  const isSuperAdmin = roles?.includes("SuperAdmin");

  const getLoanTypes = useGetLoanTypes();
  const createLoanType = useCreateLoanType();
  const updateLoanType = useUpdateLoanType();

  const [groupId, setGroupId] = useState(isSuperAdmin ? undefined : user?.Group?.id);
  const [chosenSociety, setChosenSociety] = useState(isSuperAdmin ? undefined : user?.Group);
  const [isSocietyPickerOpen, setIsSocietyPickerOpen] = useState(isSuperAdmin);

  const [loanTypes, setLoanTypes] = useState([]);
  const [loadingInit, setLoadingInit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [formErrors, setFormErrors] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchLoanTypes = async () => {
    if (!groupId) return;
    setLoadingInit(true);
    try {
      const response = await getLoanTypes(groupId);
      if (response?.payload?.status === "success") {
        setLoanTypes(response.payload.data || []);
      } else {
        toastManager.addToast({
          message: response?.payload?.message || "Failed to load loan types",
          type: "error",
        });
      }
    } catch (error) {
      toastManager.addToast({ message: "Failed to load loan types", type: "error" });
    } finally {
      setLoadingInit(false);
    }
  };

  useEffect(() => {
    fetchLoanTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openModal = (loanType = null) => {
    if (loanType) {
      setFormData({
        id: loanType.id,
        name: loanType.name,
        template: loanType.template,
        minThriftMonths: loanType.eligibilityConfig?.minThriftMonths ?? "",
        thriftPercentageCap: loanType.eligibilityConfig?.thriftPercentageCap ?? "",
        minAmount: loanType.minAmount ?? "",
        maxAmount: loanType.maxAmount ?? "",
        isActive: loanType.isActive.toString(),
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setValidationErrors([]);
    setFormErrors([]);
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    setValidationErrors([]);
    setFormErrors([]);

    const rules = [
      { input: { value: formData.name, field: "name", type: "text" }, rules: { required: true } },
      { input: { value: formData.template, field: "template", type: "text" }, rules: { required: true } },
      ...(formData.template === "standard_cooperative"
        ? [
            {
              input: { value: formData.minThriftMonths, field: "minThriftMonths", type: "number" },
              rules: { required: true },
            },
            {
              input: { value: formData.thriftPercentageCap, field: "thriftPercentageCap", type: "number" },
              rules: { required: true },
            },
          ]
        : []),
    ];

    const validate = await runValidation(rules);
    if (validate?.status === false) {
      setValidationErrors(validate.errors);
      return;
    }

    const payload = {
      id: formData.id,
      groupId,
      name: formData.name,
      template: formData.template,
      isActive: formData.isActive === "true",
      ...(formData.template === "standard_cooperative"
        ? {
            eligibilityConfig: {
              minThriftMonths: Number(formData.minThriftMonths),
              thriftPercentageCap: Number(formData.thriftPercentageCap),
            },
          }
        : {
            minAmount: formData.minAmount === "" ? null : Number(formData.minAmount),
            maxAmount: formData.maxAmount === "" ? null : Number(formData.maxAmount),
          }),
    };

    setIsSaving(true);
    try {
      const response = formData.id
        ? await updateLoanType(payload)
        : await createLoanType(payload);

      if (response?.payload?.status === "success") {
        toastManager.addToast({
          message: `Loan type successfully ${formData.id ? "updated" : "created"}`,
          type: "success",
        });
        setIsModalOpen(false);
        fetchLoanTypes();
      } else {
        setFormErrors(extractErrorMessages(response?.payload));
        toastManager.addToast({
          message: response?.payload?.message || "Something went wrong",
          type: "error",
        });
      }
    } catch (error) {
      setFormErrors(["Something went wrong"]);
      toastManager.addToast({ message: "Something went wrong", type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleActive = async (loanType) => {
    try {
      const response = await updateLoanType({ id: loanType.id, isActive: !loanType.isActive });
      if (response?.payload?.status === "success") {
        toastManager.addToast({
          message: `Loan type ${loanType.isActive ? "deactivated" : "activated"}`,
          type: "success",
        });
        fetchLoanTypes();
      } else {
        toastManager.addToast({
          message: response?.payload?.message || "Failed to update loan type",
          type: "error",
        });
      }
    } catch (error) {
      toastManager.addToast({ message: "Failed to update loan type", type: "error" });
    }
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 relative">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#003399]">Loan Types</h1>
          <p className="text-gray-600 text-sm mt-1">
            Define the loan products a society offers and the eligibility rules each one follows.
          </p>
          {isSuperAdmin && (
            <p className="mt-2">
              Current Society:{" "}
              <span className="font-semibold text-[#003399] bg-[#6699FF]/20 px-2 py-1 rounded">
                {chosenSociety?.name || "None selected"}
              </span>
            </p>
          )}
        </div>
        <div className="flex gap-3">
          {isSuperAdmin && (
            <button
              onClick={() => setIsSocietyPickerOpen(true)}
              className="btn btn-outline-secondary"
            >
              Change Society
            </button>
          )}
          <button onClick={() => openModal()} className="btn btn-primary" disabled={!groupId}>
            + Add Loan Type
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#003399]/20 rounded-xl shadow-sm overflow-hidden">
        {!groupId ? (
          <div className="p-8 text-center text-gray-500">Select a society to view its loan types.</div>
        ) : loadingInit ? (
          <div className="p-8 text-center text-gray-500">Loading loan types...</div>
        ) : loanTypes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No loan types configured yet.</div>
        ) : (
          <div className="table-responsive-custom p-4">
            <table className="table table-custom text-start table-hover w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-3">Name</th>
                  <th className="p-3">Template</th>
                  <th className="p-3">Eligibility / Cap</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Action</th>
                </tr>
              </thead>
              
              <tbody>
                {loanTypes.map((loanType) => (
                  <tr key={loanType.id} className="border-b">
                    <td className="p-3 font-medium text-[#003399]">{loanType.name}</td>
                    <td className="p-3">
                      {TEMPLATE_OPTIONS.find((t) => t.value === loanType.template)?.label || loanType.template}
                    </td>
                    <td className="p-3">
                      {loanType.template === "standard_cooperative"
                        ? `Min ${loanType.eligibilityConfig?.minThriftMonths ?? "-"}mo, cap ${
                            loanType.eligibilityConfig?.thriftPercentageCap ?? "-"
                          }%`
                        : `Min ${loanType.minAmount ?? "—"} / Max ${loanType.maxAmount ?? "—"}`}
                    </td>
                    <td className="p-3">
                      <span className={`badge ${loanType.isActive ? "bg-success" : "bg-secondary"}`}>
                        {loanType.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => openModal(loanType)} className="btn btn-sm btn-outline-primary">
                        Edit
                      </button>{" "}
                      <button
                        onClick={() => toggleActive(loanType)}
                        className={`btn btn-sm ${loanType.isActive ? "btn-outline-danger" : "btn-outline-success"}`}
                      >
                        {loanType.isActive ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="container bg-grey society-setting">
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <div className="modal__withdraw1">
            <div className="border-b pb-3 mb-4">
              <h3 className="text-xl font-bold text-[#003399]">
                {formData.id ? "Edit Loan Type" : "Add Loan Type"}
              </h3>
            </div>

            {formErrors.length > 0 && (
              <div className="bg-red-50 border border-red-300 text-red-700 rounded-md p-3 mb-3 text-sm">
                <ul className="list-disc pl-4 mb-0">
                  {formErrors.map((message, index) => (
                    <li key={index}>{message}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="row mb-3">
              <div className="col-md-6 px-2 mb-3">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    placeholder="e.g. Standard Cooperative Loan"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                  <ValidationError validationErrors={validationErrors} field="name" />
                </div>
              </div>

              <div className="col-md-6 px-2 mb-3">
                <div className="form-group">
                  <label className="form-label">Template</label>
                  <select
                    name="template"
                    className="form-select"
                    value={formData.template}
                    onChange={handleInputChange}
                  >
                    {TEMPLATE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ValidationError validationErrors={validationErrors} field="template" />
                </div>
              </div>

              {formData.template === "standard_cooperative" ? (
                <>
                  <div className="col-md-6 px-2 mb-3">
                    <div className="form-group">
                      <label className="form-label">Minimum months of thrift contribution</label>
                      <input
                        type="number"
                        name="minThriftMonths"
                        className="form-control"
                        placeholder="e.g. 6"
                        value={formData.minThriftMonths}
                        onChange={handleInputChange}
                      />
                      <ValidationError validationErrors={validationErrors} field="minThriftMonths" />
                    </div>
                  </div>
                  <div className="col-md-6 px-2 mb-3">
                    <div className="form-group">
                      <label className="form-label">Loan cap (% of cumulative thrift paid)</label>
                      <input
                        type="number"
                        name="thriftPercentageCap"
                        className="form-control"
                        placeholder="e.g. 50"
                        value={formData.thriftPercentageCap}
                        onChange={handleInputChange}
                      />
                      <ValidationError validationErrors={validationErrors} field="thriftPercentageCap" />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="col-md-6 px-2 mb-3">
                    <div className="form-group">
                      <label className="form-label">Minimum amount (optional)</label>
                      <input
                        type="number"
                        name="minAmount"
                        className="form-control"
                        value={formData.minAmount}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-6 px-2 mb-3">
                    <div className="form-group">
                      <label className="form-label">Maximum amount (optional)</label>
                      <input
                        type="number"
                        name="maxAmount"
                        className="form-control"
                        value={formData.maxAmount}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="col-md-6 px-2 mb-3">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    name="isActive"
                    className="form-select"
                    value={formData.isActive}
                    onChange={handleInputChange}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
              <button
                className="btn btn-secondary"
                onClick={() => setIsModalOpen(false)}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={isSaving}>
                {isSaving ? <ClipLoader color="#fff" size={20} /> : "Save"}
              </button>
            </div>
          </div>
        </Modal>
      </div>

      {isSuperAdmin && (
        <Modal
          isOpen={isSocietyPickerOpen}
          onClose={() => setIsSocietyPickerOpen(false)}
          children={
            <SelectSociety
              setGroupId={setGroupId}
              setIsOpen={setIsSocietyPickerOpen}
              setChosenSociety={setChosenSociety}
            />
          }
        />
      )}
    </div>
  );
};

export default LoanTypes;
